import React, { useState } from 'react';
import { NotificationSettings, EmergencyContact, SafetySeverity } from './types';
import { Bell, Mail, Phone, Clock, Users, Plus, Trash2, Save } from 'lucide-react';

interface SettingsTabProps {
  settings: NotificationSettings;
  onSaveSettings: (settings: NotificationSettings) => void;
}

const SettingsTab: React.FC<SettingsTabProps> = ({ settings, onSaveSettings }) => {
  // Use state to track form values
  const [formValues, setFormValues] = useState<NotificationSettings>(settings);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Handle toggle changes
  const handleToggleChange = (field: keyof NotificationSettings) => {
    setFormValues(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  
  // Handle severity threshold change
  const handleSeverityChange = (value: SafetySeverity) => {
    setFormValues(prev => ({
      ...prev,
      notificationThreshold: value
    }));
  };
  
  // Handle time changes
  const handleTimeChange = (
    timeType: 'digestTime' | 'quietHours.start' | 'quietHours.end',
    field: 'hour' | 'minute',
    value: number
  ) => {
    if (timeType === 'digestTime') {
      setFormValues(prev => ({
        ...prev,
        digestTime: {
          ...prev.digestTime,
          [field]: value
        }
      }));
    } else if (timeType === 'quietHours.start') {
      setFormValues(prev => ({
        ...prev,
        quietHours: {
          ...prev.quietHours,
          start: {
            ...prev.quietHours.start,
            [field]: value
          }
        }
      }));
    } else if (timeType === 'quietHours.end') {
      setFormValues(prev => ({
        ...prev,
        quietHours: {
          ...prev.quietHours,
          end: {
            ...prev.quietHours.end,
            [field]: value
          }
        }
      }));
    }
  };
  
  // Handle emergency contact changes
  const handleContactChange = (
    index: number,
    field: keyof EmergencyContact,
    value: any
  ) => {
    setFormValues(prev => {
      const updatedContacts = [...prev.emergencyContacts];
      updatedContacts[index] = {
        ...updatedContacts[index],
        [field]: value
      };
      return {
        ...prev,
        emergencyContacts: updatedContacts
      };
    });
  };
  
  // Add new emergency contact
  const handleAddContact = () => {
    setFormValues(prev => ({
      ...prev,
      emergencyContacts: [
        ...prev.emergencyContacts,
        {
          id: `contact-${Date.now()}`,
          name: '',
          relationship: '',
          phone: '',
          email: '',
          notificationPreference: 'IMMEDIATE',
          notifyOnSeverity: ['HIGH']
        }
      ]
    }));
  };
  
  // Remove emergency contact
  const handleRemoveContact = (index: number) => {
    setFormValues(prev => {
      const updatedContacts = [...prev.emergencyContacts];
      updatedContacts.splice(index, 1);
      return {
        ...prev,
        emergencyContacts: updatedContacts
      };
    });
  };
  
  // Toggle severity notification for a contact
  const toggleContactSeverity = (contactIndex: number, severity: SafetySeverity) => {
    setFormValues(prev => {
      const updatedContacts = [...prev.emergencyContacts];
      const contact = updatedContacts[contactIndex];
      
      if (contact.notifyOnSeverity.includes(severity)) {
        contact.notifyOnSeverity = contact.notifyOnSeverity.filter(s => s !== severity);
      } else {
        contact.notifyOnSeverity = [...contact.notifyOnSeverity, severity];
      }
      
      return {
        ...prev,
        emergencyContacts: updatedContacts
      };
    });
  };
  
  // Format time for display
  const formatTime = (hour: number, minute: number): string => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    const displayMinute = minute.toString().padStart(2, '0');
    return `${displayHour}:${displayMinute} ${period}`;
  };
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSaveSettings(formValues);
    setShowSuccess(true);
    
    // Hide success message after 3 seconds
    setTimeout(() => {
      setShowSuccess(false);
    }, 3000);
  };
  
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold">Notification Settings</h3>
        <p className="text-sm text-gray-500">Customize how and when you receive alerts</p>
      </div>
      
      <form onSubmit={handleSubmit} className="p-6">
        {/* Success Message */}
        {showSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-700 flex items-center">
              <Save className="mr-2" size={18} />
              Settings saved successfully!
            </p>
          </div>
        )}
        
        {/* Notification Methods */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-4">Notification Methods</h4>
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="emailNotifications"
                checked={formValues.emailNotifications}
                onChange={() => handleToggleChange('emailNotifications')}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <label htmlFor="emailNotifications" className="ml-2 flex items-center text-sm text-gray-700">
                <Mail size={16} className="mr-2 text-gray-500" />
                Email Notifications
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="smsNotifications"
                checked={formValues.smsNotifications}
                onChange={() => handleToggleChange('smsNotifications')}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <label htmlFor="smsNotifications" className="ml-2 flex items-center text-sm text-gray-700">
                <Phone size={16} className="mr-2 text-gray-500" />
                SMS Text Notifications
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="pushNotifications"
                checked={formValues.pushNotifications}
                onChange={() => handleToggleChange('pushNotifications')}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <label htmlFor="pushNotifications" className="ml-2 flex items-center text-sm text-gray-700">
                <Bell size={16} className="mr-2 text-gray-500" />
                Mobile Push Notifications
              </label>
            </div>
          </div>
        </div>
        
        {/* Alert Threshold */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-4">Alert Threshold</h4>
          <p className="text-xs text-gray-500 mb-2">Receive notifications for alerts at or above this severity level</p>
          
          <div className="flex space-x-4">
            {(['HIGH', 'MEDIUM', 'LOW'] as SafetySeverity[]).map(severity => (
              <div key={severity} className="flex items-center">
                <input
                  type="radio"
                  id={`severity-${severity}`}
                  name="notificationThreshold"
                  checked={formValues.notificationThreshold === severity}
                  onChange={() => handleSeverityChange(severity)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <label 
                  htmlFor={`severity-${severity}`} 
                  className={`ml-2 text-sm font-medium ${
                    severity === 'HIGH' ? 'text-red-700' :
                    severity === 'MEDIUM' ? 'text-yellow-700' :
                    'text-green-700'
                  }`}
                >
                  {severity}
                </label>
              </div>
            ))}
          </div>
        </div>
        
        {/* Notification Preferences */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-4">Notification Preferences</h4>
          
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="dailyDigest"
                checked={formValues.dailyDigest}
                onChange={() => handleToggleChange('dailyDigest')}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <label htmlFor="dailyDigest" className="ml-2 text-sm text-gray-700">
                Send daily digest of all alerts
              </label>
            </div>
            
            {formValues.dailyDigest && (
              <div className="ml-6 flex items-center">
                <label htmlFor="digestTime" className="text-sm text-gray-700 mr-2">
                  Digest delivery time:
                </label>
                <select
                  id="digestTimeHour"
                  value={formValues.digestTime.hour}
                  onChange={(e) => handleTimeChange('digestTime', 'hour', parseInt(e.target.value))}
                  className="mr-1 text-sm border-gray-300 rounded-md shadow-sm"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i % 12 || 12}</option>
                  ))}
                </select>
                <span className="mx-1">:</span>
                <select
                  id="digestTimeMinute"
                  value={formValues.digestTime.minute}
                  onChange={(e) => handleTimeChange('digestTime', 'minute', parseInt(e.target.value))}
                  className="mr-1 text-sm border-gray-300 rounded-md shadow-sm"
                >
                  {Array.from({ length: 60 }, (_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                  ))}
                </select>
                <select
                  id="digestTimePeriod"
                  value={formValues.digestTime.hour >= 12 ? 'PM' : 'AM'}
                  onChange={(e) => {
                    const newHour = e.target.value === 'PM' 
                      ? (formValues.digestTime.hour % 12) + 12 
                      : formValues.digestTime.hour % 12;
                    handleTimeChange('digestTime', 'hour', newHour);
                  }}
                  className="text-sm border-gray-300 rounded-md shadow-sm"
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            )}
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="notifyOnAcknowledged"
                checked={formValues.notifyOnAcknowledged}
                onChange={() => handleToggleChange('notifyOnAcknowledged')}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <label htmlFor="notifyOnAcknowledged" className="ml-2 text-sm text-gray-700">
                Notify when alerts are acknowledged
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="notifyOnResolved"
                checked={formValues.notifyOnResolved}
                onChange={() => handleToggleChange('notifyOnResolved')}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <label htmlFor="notifyOnResolved" className="ml-2 text-sm text-gray-700">
                Notify when alerts are resolved
              </label>
            </div>
          </div>
        </div>
        
        {/* Quiet Hours */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-medium text-gray-700">Quiet Hours</h4>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="quietHoursEnabled"
                checked={formValues.quietHours.enabled}
                onChange={() => {
                  setFormValues(prev => ({
                    ...prev,
                    quietHours: {
                      ...prev.quietHours,
                      enabled: !prev.quietHours.enabled
                    }
                  }));
                }}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <label htmlFor="quietHoursEnabled" className="ml-2 text-sm text-gray-700">
                Enable Quiet Hours
              </label>
            </div>
          </div>
          
          {formValues.quietHours.enabled && (
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Start Time</label>
                  <div className="flex items-center">
                    <select
                      value={formValues.quietHours.start.hour}
                      onChange={(e) => handleTimeChange('quietHours.start', 'hour', parseInt(e.target.value))}
                      className="mr-1 text-sm border-gray-300 rounded-md shadow-sm"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{i % 12 || 12}</option>
                      ))}
                    </select>
                    <span className="mx-1">:</span>
                    <select
                      value={formValues.quietHours.start.minute}
                      onChange={(e) => handleTimeChange('quietHours.start', 'minute', parseInt(e.target.value))}
                      className="mr-1 text-sm border-gray-300 rounded-md shadow-sm"
                    >
                      {Array.from({ length: 60 }, (_, i) => (
                        <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                      ))}
                    </select>
                    <select
                      value={formValues.quietHours.start.hour >= 12 ? 'PM' : 'AM'}
                      onChange={(e) => {
                        const newHour = e.target.value === 'PM' 
                          ? (formValues.quietHours.start.hour % 12) + 12 
                          : formValues.quietHours.start.hour % 12;
                        handleTimeChange('quietHours.start', 'hour', newHour);
                      }}
                      className="text-sm border-gray-300 rounded-md shadow-sm"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 mb-2">End Time</label>
                  <div className="flex items-center">
                    <select
                      value={formValues.quietHours.end.hour}
                      onChange={(e) => handleTimeChange('quietHours.end', 'hour', parseInt(e.target.value))}
                      className="mr-1 text-sm border-gray-300 rounded-md shadow-sm"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{i % 12 || 12}</option>
                      ))}
                    </select>
                    <span className="mx-1">:</span>
                    <select
                      value={formValues.quietHours.end.minute}
                      onChange={(e) => handleTimeChange('quietHours.end', 'minute', parseInt(e.target.value))}
                      className="mr-1 text-sm border-gray-300 rounded-md shadow-sm"
                    >
                      {Array.from({ length: 60 }, (_, i) => (
                        <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                      ))}
                    </select>
                    <select
                      value={formValues.quietHours.end.hour >= 12 ? 'PM' : 'AM'}
                      onChange={(e) => {
                        const newHour = e.target.value === 'PM' 
                          ? (formValues.quietHours.end.hour % 12) + 12 
                          : formValues.quietHours.end.hour % 12;
                        handleTimeChange('quietHours.end', 'hour', newHour);
                      }}
                      className="text-sm border-gray-300 rounded-md shadow-sm"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm text-gray-700 mb-2">
                  Override for Severity Level
                </label>
                <div className="flex space-x-4">
                  {(['HIGH', 'MEDIUM', 'LOW'] as SafetySeverity[]).map(severity => (
                    <div key={severity} className="flex items-center">
                      <input
                        type="radio"
                        id={`override-${severity}`}
                        name="quietHoursOverride"
                        checked={formValues.quietHours.overrideForSeverity === severity}
                        onChange={() => {
                          setFormValues(prev => ({
                            ...prev,
                            quietHours: {
                              ...prev.quietHours,
                              overrideForSeverity: severity
                            }
                          }));
                        }}
                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <label 
                        htmlFor={`override-${severity}`} 
                        className={`ml-2 text-sm font-medium ${
                          severity === 'HIGH' ? 'text-red-700' :
                          severity === 'MEDIUM' ? 'text-yellow-700' :
                          'text-green-700'
                        }`}
                      >
                        {severity}
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Alerts with this severity or higher will still notify during quiet hours
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Emergency Contacts */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-medium text-gray-700">Emergency Contacts</h4>
            <button
              type="button"
              onClick={handleAddContact}
              className="flex items-center text-sm text-blue-600 hover:text-blue-800"
            >
              <Plus size={16} className="mr-1" />
              Add Contact
            </button>
          </div>
          
          {formValues.emergencyContacts.length > 0 ? (
            <div className="space-y-6">
              {formValues.emergencyContacts.map((contact, index) => (
                <div key={contact.id} className="bg-gray-50 p-4 rounded-md">
                  <div className="flex justify-between items-start mb-4">
                    <h5 className="text-sm font-medium">Contact #{index + 1}</h5>
                    <button
                      type="button"
                      onClick={() => handleRemoveContact(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={contact.name}
                        onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                        className="w-full text-sm border-gray-300 rounded-md shadow-sm"
                        placeholder="Full Name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Relationship</label>
                      <input
                        type="text"
                        value={contact.relationship}
                        onChange={(e) => handleContactChange(index, 'relationship', e.target.value)}
                        className="w-full text-sm border-gray-300 rounded-md shadow-sm"
                        placeholder="e.g. Son, Daughter, Nurse"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={contact.phone}
                        onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                        className="w-full text-sm border-gray-300 rounded-md shadow-sm"
                        placeholder="Phone Number"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={contact.email}
                        onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                        className="w-full text-sm border-gray-300 rounded-md shadow-sm"
                        placeholder="Email Address"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <label className="block text-sm text-gray-700 mb-1">Notification Frequency</label>
                    <select
                      value={contact.notificationPreference}
                      onChange={(e) => handleContactChange(index, 'notificationPreference', e.target.value)}
                      className="w-full text-sm border-gray-300 rounded-md shadow-sm"
                    >
                      <option value="IMMEDIATE">Immediate</option>
                      <option value="HOURLY">Hourly Digest</option>
                      <option value="DAILY">Daily Digest</option>
                      <option value="WEEKLY">Weekly Digest</option>
                    </select>
                  </div>
                  
                  <div className="mt-4">
                    <label className="block text-sm text-gray-700 mb-2">Notify For Severity</label>
                    <div className="flex space-x-4">
                      {(['HIGH', 'MEDIUM', 'LOW'] as SafetySeverity[]).map(severity => (
                        <div key={severity} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`contact-${index}-severity-${severity}`}
                            checked={contact.notifyOnSeverity.includes(severity)}
                            onChange={() => toggleContactSeverity(index, severity)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300"
                          />
                          <label 
                            htmlFor={`contact-${index}-severity-${severity}`} 
                            className={`ml-2 text-sm font-medium ${
                              severity === 'HIGH' ? 'text-red-700' :
                              severity === 'MEDIUM' ? 'text-yellow-700' :
                              'text-green-700'
                            }`}
                          >
                            {severity}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 p-6 rounded-md text-center">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No emergency contacts</h3>
              <p className="mt-1 text-sm text-gray-500">Add emergency contacts who should be notified of safety alerts</p>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleAddContact}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                  Add Contact
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <Save className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Save Settings
          </button>
        </div>
      </form>
    </div>
  );
};

export default SettingsTab; 